import { BodyText } from '@postscript/components';
import React from 'react';
import styled, { css } from 'styled-components';

// Shared visual contract for every detail row in the drawer. Interactive
// rows render as anchor placeholders (callers wire up `onNavigate` via the
// drawer's navigation map to open the Brand Center route in a new tab).
// Passing `readOnly` collapses the row to a plain div for sections like
// Subscriber Details where every value is display-only.
//
// Borders use `:not(:last-child)` so the final row drops its bottom border
// and lets the SectionCard's own border close the column without doubling.

// Left inset that aligns row labels with the section header text. Shared by
// every row-shaped surface in the drawer (Row, ExpandableRow, the Message
// Inputs placeholder) so the alignment can't silently drift.
export const ROW_LEFT_INSET = '13px';

const rowLayout = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) ${ROW_LEFT_INSET};
  background: var(--surface-bkg-color);

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color-dim);
  }
`;

const interactiveSurface = css`
  ${rowLayout};
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color 80ms ease;

  &,
  &:hover,
  &:focus,
  &:focus-visible,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover {
    background: var(--gray-1);
  }

  &:active,
  &[data-pressed='true'] {
    background: var(--gray-2);
  }

  &:focus-visible {
    outline: 2px solid var(--link-color);
    outline-offset: -2px;
  }
`;

const RowAnchor = styled.a`
  ${interactiveSurface};
`;

const StaticRow = styled.div`
  ${rowLayout};
`;

const ValueColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--spacing-1);
  text-align: right;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

// The label never compresses — only the value side shrinks so long,
// unbreakable values (e.g. Content URLs) truncate instead of blowing out
// the layout.
const Label = styled(BodyText).attrs({ size: 'small', bold: true })`
  flex-shrink: 0;
`;

interface RowProps {
  label: string;
  children: React.ReactNode;
  // Optional handler. When omitted the row is still rendered as a clickable
  // anchor placeholder so the user can see the hover affordance, but the
  // click is a no-op until a dev fills in `href`.
  onNavigate?: () => void;
  // Optional target URL. Defaults to a placeholder so dev work can land in a
  // follow-up PR without restructuring the markup.
  href?: string;
  // Render as a non-interactive row (no hover/active/focus, no anchor) for
  // display-only sections like Subscriber Details.
  readOnly?: boolean;
}

export const Row: React.FC<RowProps> = ({
  label,
  children,
  onNavigate,
  href = '#',
  readOnly = false,
}) => {
  const body = (
    <>
      <Label>{label}</Label>
      <ValueColumn>{children}</ValueColumn>
    </>
  );

  if (readOnly) {
    return <StaticRow>{body}</StaticRow>;
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // The `#` placeholder href would scroll to the top of the page — block it
    // until a real route is wired in. Real navigations should pass an `href`
    // (Brand Center deep link) and the anchor's native behavior kicks in.
    if (href === '#') e.preventDefault();
    onNavigate?.();
  };

  return (
    <RowAnchor href={href} onClick={handleClick}>
      {body}
    </RowAnchor>
  );
};
